import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, Eye, EyeOff, Send, ShieldCheck, FileCheck, CheckCircle, Check, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { homePathForRole } from '../utils/roleHome';
import { authApi } from '../api/client';
import './AuthPage.css';

const strongPassword = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;

export default function ResetPasswordPage() {
  const { t } = useLanguage();
  const { user, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && isAuthenticated && user) {
      navigate(homePathForRole(user.role) || '/', { replace: true });
    }
  }, [loading, isAuthenticated, user, navigate]);
  const [params] = useSearchParams();
  const [token] = useState(params.get('token') || '');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [ok, setOk] = useState(false);
  const [touched, setTouched] = useState(false);

  const validate = () => {
    if (!token.trim()) return t.auth.invalidToken || 'Lien invalide ou expiré';
    if (!password || !confirm) return t.auth.invalidPayload || 'Veuillez remplir tous les champs obligatoires.';
    if (!strongPassword.test(password)) return t.auth.passwordHint;
    if (password !== confirm) return t.auth.passwordsMismatch;
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched(true);
    const msg = validate();
    if (msg) {
      setError(msg);
      return;
    }
    setError('');
    try {
      await authApi.resetPassword(token, password);
      setOk(true);
      setTimeout(() => navigate('/login', { replace: true }), 1500);
    } catch (err) {
      setError(t.auth[err.data?.error] || t.auth.invalidToken || t.auth.invalidCredentials);
    }
  };

  useEffect(() => {
    if (!touched) return;
    const msg = validate();
    setError(msg);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [touched, password, confirm, token]);

  const perks = [
    { icon: ShieldCheck, text: t.auth.perkSecure },
    { icon: FileCheck, text: t.auth.perkOrders },
    { icon: CheckCircle, text: t.auth.perkDocs },
  ];

  return (
    <div className="auth-page">
      <div className="auth-shell">
        <aside className="auth-brand-panel">
          <div className="auth-brand-glow" aria-hidden="true" />
          <div className="auth-brand-content">
            <p className="auth-brand-name">
              Tarjemlek
              <span lang="ar">ترجملك</span>
            </p>
            <h1>{t.auth.resetTitle}</h1>
            <p className="auth-brand-text">{t.auth.resetHint}</p>
            <ul className="auth-perks">
              {perks.map(({ icon: Icon, text }) => (
                <li key={text}>
                  <Icon size={18} aria-hidden="true" />
                  <span>{text}</span>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        <div className="auth-form-panel">
          <form className="auth-card" onSubmit={handleSubmit} noValidate>
            <div className="auth-card-head">
              <h2>{t.auth.resetTitle}</h2>
              <p>{t.auth.passwordHint}</p>
            </div>

            {error && <div className="auth-error" role="alert">{error}</div>}
            {ok && <div className="auth-success" role="status">{t.auth.resetDone}</div>}

            <div className="auth-field">
              <label htmlFor="reset-password">{t.auth.newPassword}</label>
              <div className="auth-input">
                <Lock size={17} aria-hidden="true" />
                <input
                  id="reset-password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  autoComplete="new-password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setTouched(true); }}
                />
                <button
                  type="button"
                  className="auth-eye"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? t.auth.hidePassword : t.auth.showPassword}
                >
                  {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                </button>
              </div>
            </div>

            <div className="auth-field">
              <label htmlFor="reset-confirm">{t.auth.confirmNewPassword}</label>
              <div className="auth-input">
                <Lock size={17} aria-hidden="true" />
                <input
                  id="reset-confirm"
                  type={showConfirm ? 'text' : 'password'}
                  required
                  autoComplete="new-password"
                  placeholder="••••••••"
                  value={confirm}
                  onChange={(e) => { setConfirm(e.target.value); setTouched(true); }}
                />
                <button
                  type="button"
                  className="auth-eye"
                  onClick={() => setShowConfirm((v) => !v)}
                  aria-label={showConfirm ? t.auth.hidePassword : t.auth.showPassword}
                >
                  {showConfirm ? <EyeOff size={17} /> : <Eye size={17} />}
                </button>
              </div>
            </div>

            <div className="auth-pwd-conditions" style={{ margin: '12px 0', fontSize: '0.85rem' }}>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {[
                  { key: 'min', test: password.length >= 8 },
                  { key: 'upper', test: /[A-Z]/.test(password) },
                  { key: 'lower', test: /[a-z]/.test(password) },
                  { key: 'number', test: /\d/.test(password) },
                  { key: 'special', test: /[^A-Za-z0-9]/.test(password) },
                ].map(({ key, test }) => (
                  <li
                    key={key}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      marginBottom: 4,
                      color: test ? '#16a34a' : '#64748b',
                      transition: 'color 0.2s ease',
                    }}
                  >
                    {test ? <Check size={14} /> : <X size={14} />}
                    <span>{t.auth[`passwordReq${key.charAt(0).toUpperCase() + key.slice(1)}`]}</span>
                  </li>
                ))}
              </ul>
            </div>

            <button type="submit" className="btn btn-primary auth-submit">
              <Send size={17} />
              {t.auth.resetButton}
            </button>

            <p className="auth-switch">
              <Link to="/login">{t.auth.loginLink}</Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
